"use client";
import { cn } from "@/lib/utils";
import React, { useEffect, useState, useRef } from "react";

interface ShootingStar {
  id: number;
  x: number;
  y: number;
  angle: number;
  scale: number;
  speed: number;
  distance: number;
}

interface ShootingStarsProps {
  minSpeed?: number;
  maxSpeed?: number;
  minDelay?: number;
  maxDelay?: number;
  starColor?: string;
  trailColor?: string;
  starWidth?: number;
  starHeight?: number;
  className?: string;
}

const getRandomStartPoint = () => {
  const side = Math.floor(Math.random() * 4);
  const offset = Math.random() * window.innerWidth;

  switch (side) {
    case 0:
      return { x: offset, y: 0, angle: 45 };
    case 1:
      return { x: window.innerWidth, y: offset, angle: 135 };
    case 2:
      return { x: offset, y: window.innerHeight, angle: 225 };
    case 3:
      return { x: 0, y: offset, angle: 315 };
    default:
      return { x: 0, y: 0, angle: 45 };
  }
};

export const ShootingStars: React.FC<ShootingStarsProps> = ({
  minSpeed = 12,
  maxSpeed = 28,
  minDelay = 1200,
  maxDelay = 4200,
  starColor = "#FCE7F3", // soft highlight
  trailColor = "#6366F1", // brand-ish purple/indigo
  starWidth = 14,
  starHeight = 0.5,
  className,
}) => {
  const [star, setStar] = useState<ShootingStar | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const createStar = () => {
      const { x, y, angle } = getRandomStartPoint();
      const newStar: ShootingStar = {
        id: Date.now(),
        x,
        y,
        angle,
        scale: 1,
        speed: Math.random() * (maxSpeed - minSpeed) + minSpeed,
        distance: 0,
      };
      setStar(newStar);

      const randomDelay = Math.random() * (maxDelay - minDelay) + minDelay;
      setTimeout(createStar, randomDelay);
    };

    createStar();

    return () => {
      // no-op cleanup; timeouts naturally end
    };
  }, [minSpeed, maxSpeed, minDelay, maxDelay]);

  useEffect(() => {
    const moveStar = () => {
      if (star) {
        setStar((prevStar) => {
          if (!prevStar) return null;
          const newX =
            prevStar.x +
            prevStar.speed * Math.cos((prevStar.angle * Math.PI) / 180);
          const newY =
            prevStar.y +
            prevStar.speed * Math.sin((prevStar.angle * Math.PI) / 180);
          const newDistance = prevStar.distance + prevStar.speed;
          // Keep scale growth subtle so the trail stays thin and sharp
          const newScale = 1 + newDistance / 250;
          if (
            newX < -20 ||
            newX > window.innerWidth + 20 ||
            newY < -20 ||
            newY > window.innerHeight + 20
          ) {
            return null;
          }
          return {
            ...prevStar,
            x: newX,
            y: newY,
            distance: newDistance,
            scale: newScale,
          };
        });
      }
    };

    const animationFrame = requestAnimationFrame(moveStar);
    return () => cancelAnimationFrame(animationFrame);
  }, [star]);

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden",
        className
      )}
    >
      {star && (
        <svg
          ref={svgRef}
          className="absolute"
          style={{
            left: star.x,
            top: star.y,
            transform: `translate(-50%, -50%) rotate(${star.angle}deg) scale(${star.scale})`,
          }}
          width={starWidth}
          height={starWidth}
          viewBox={`0 0 ${starWidth} ${starWidth}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="shootingStarGradient"
              x1="0%"
              y1="50%"
              x2="100%"
              y2="50%"
            >
              <stop offset="0%" stopColor={trailColor} stopOpacity="0" />
              <stop offset="50%" stopColor={trailColor} stopOpacity="0.5" />
              <stop offset="100%" stopColor={starColor} stopOpacity="1" />
            </linearGradient>
          </defs>
          <g>
            <circle
              cx={starWidth - starHeight * 2}
              cy={starWidth / 2}
              r={starHeight * 1.2}
              fill={starColor}
            />
            <rect
              x="0"
              y={starWidth / 2 - starHeight / 2}
              width={starWidth - starHeight}
              height={starHeight}
              fill="url(#shootingStarGradient)"
            />
          </g>
        </svg>
      )}
    </div>
  );
};

